import {
  addMonths,
  format,
  getDay,
  isAfter,
  isBefore,
  isToday,
  parseISO,
  subMinutes,
} from "date-fns";
import differenceInYears from "date-fns/differenceInYears";
import isWithinInterval from "date-fns/isWithinInterval";
import DOMPurify from "dompurify";
import {
  FamilyMember,
  GenericObject,
  GQLError,
  LifeEvent,
  SelectOption,
  SelectOptionNum,
} from "./commonTypings";
import {
  EnrollmentDependent,
  EnrollmentSummary,
  EnrollmentSummaryAdditionalInsurance,
  EnrollmentSummaryPlanOptions,
  PlanDocumentsResponse,
} from "./Enrollment/typings";

export const sanitizeHtml = (val: string) => {
  return DOMPurify.sanitize(
    val,
    val?.match(/target=/) ? { ADD_ATTR: ["target"] } : undefined,
  );
};

export const formatPhoneNumber = (val: string) => {
  val = stripNonDigits(val);
  return val.replace(/1?(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
};

export const formatZip = (val: string) => {
  if (typeof val !== "undefined" && val !== null) {
    if (zipUsIsValid(val)) {
      return val;
    }
    val = val + "";
    if (val.length > 5) {
      if (val.match(/.*0000/)) {
        return val.substring(0, 5);
      } else {
        return val.substring(0, 5) + "-" + val.substring(5);
      }
    }
  }
  return val;
};

export const formatZipForApi = (val: string) => {
  if (!val) return "";
  return val.toUpperCase().replace(/[ -]/g, "");
};

export const phoneNumberIsValid = (val: string) => {
  val = stripNonDigits(val);
  return /(^\d{10}$)/.test(val);
};

export const isValidPhone = (val: string) => {
  return /^[(]{0,1}[0-9]{3}[)]{0,1}[-\s.]{0,1}[0-9]{3}[-\s.]{0,1}[0-9]{4}$/.test(
    val,
  );
};

export const ssnIsValid = (val: string) => {
  if (String(val.substring(0, 3)) === "000") {
    return false;
  }
  return /^[0-9]{3}-?[0-9]{2}-?[0-9]{4}$/.test(val);
};

export const isLast4SSNValid = (val: string) => {
  val = stripNonDigits(val);
  return /(^\d{4}$)/.test(val);
};

export const zipUsIsValid = (val: string) => {
  return /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val);
};

export const zipCanadaIsValid = (val: string) => {
  return /^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z][ -]?[0-9][ABCEGHJ-NPRSTV-Z][0-9]$/i.test(
    val,
  );
};

export const zipUsOrCanadaIsValid = (val: string) => {
  return zipUsIsValid(val) || zipCanadaIsValid(val);
};

export const isDOBValid = (val: string) => {
  return /^((0?[13578]|10|12)(-|\/)(([1-9])|(0[1-9])|([12])([0-9]?)|(3[01]?))(-|\/)((19)([2-9])(\d{1})|(20)([01])(\d{1})|([8901])(\d{1}))|(0?[2469]|11)(-|\/)(([1-9])|(0[1-9])|([12])([0-9]?)|(3[0]?))(-|\/)((19)([2-9])(\d{1})|(20)([01])(\d{1})|([8901])(\d{1})))$/.test(
    val,
  );
};

export const emailIsValid = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const memberIdIsValid = (val: string) => {
  if (String(val.substring(0, 3)) === "000") {
    return false;
  }
  val = val.replace(/\D/g, "");
  return /^\d{9}$/i.test(val);
};

export const usernameIsValid = (val: string) => {
  return /^([@.a-zA-Z0-9_-]){8,30}$/.test(val);
};

export const luNumberIsValid = (val: string) => {
  return /^\d{1,5}[da-zA-Z]?$/.test(val);
};

export const stripNonDigits = (val: string) => {
  if (val) {
    return val.replace(/[^\d]/g, "");
  }
  return "";
};

export const constisValidDate = (testdate: string) => {
  const date_regex =
    /^(?!0+\b)(\d{1,2})[/.-](?!0+\b)(\d{1,2})[/.-](\d{4}|\d{2})$/;
  return date_regex.test(testdate);
};

export const getGrapqlErrorCode = (errorData: GQLError) => {
  const { graphQLErrors } = JSON.parse(JSON.stringify(errorData));
  return graphQLErrors[0].data.code;
};

export const getGrapqlErrorMessage = (errorData: GQLError) => {
  let errMsg = "Something unexpected happened, please contact our help desk.";
  const {
    networkError: {
      result: { SystemMessages },
    },
  } = JSON.parse(JSON.stringify(errorData));

  if (SystemMessages && SystemMessages[0].Message) {
    errMsg = SystemMessages[0].Message;
  }

  return errMsg;
};

export const getGrapqlErrorRef = (errorData: GQLError) => {
  const { graphQLErrors } = JSON.parse(JSON.stringify(errorData));
  return graphQLErrors[0] && graphQLErrors[0].data
    ? graphQLErrors[0].data.errorRef
    : "Something unexpected happened, please contact our help desk. ";
};

export const sortProperties = (
  obj: any,
  sortedBy?: any,
  isNumericSort?: boolean,
  reverse?: boolean,
) => {
  sortedBy = sortedBy || 1; // by default first key
  isNumericSort = isNumericSort || false; // by default text sort
  reverse = reverse || false; // by default no reverse

  const reversed = reverse ? -1 : 1;

  const sortable = [];
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      sortable.push([key, obj[key]]);
    }
  }
  if (isNumericSort)
    sortable.sort(function (a, b) {
      return reversed * (a[1][sortedBy] - b[1][sortedBy]);
    });
  else
    sortable.sort(function (a, b) {
      const x = a[1][sortedBy].toLowerCase(),
        y = b[1][sortedBy].toLowerCase();
      return x < y ? reversed * -1 : x > y ? reversed : 0;
    });
  return sortable; // array in format [ [ key1, val1 ], [ key2, val2 ], ... ]
};

export const convertUTCDateToLocalDate = (date: Date) => {
  const newDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60 * 1000,
  );
  return newDate;
};

export const formatDate = (origDate: Date) => {
  origDate = convertUTCDateToLocalDate(origDate);
  const d = new Date();
  // @ts-ignore fixme
  const curr_yr = d.getYear();
  const date = origDate.toDateString().split(" ");

  if (origDate.getUTCFullYear() == curr_yr) {
    return date[1] + " " + date[2];
  } else {
    const d = origDate.getUTCDate();
    const m = origDate.getUTCMonth() + 1;
    const y = origDate.getUTCFullYear();

    return m + "/" + d + "/" + y;
  }
};

export const formatTime = (origDate: Date) => {
  origDate = convertUTCDateToLocalDate(origDate);
  const d = new Date();
  const curr_yr = d.getUTCFullYear();
  const temp = origDate.toLocaleTimeString().split(" ");
  const time = temp[0].split(":");

  if (origDate.getUTCFullYear() == curr_yr) {
    return "(" + time[0] + ":" + time[1] + " " + temp[1] + ")";
  } else {
    // let d = origDate.getUTCDate();
    // let m = origDate.getUTCMonth() + 1;
    // let y = origDate.getUTCFullYear();

    return "(" + time[0] + ":" + time[1] + " " + temp[1] + ")";
  }
};

// export const getParameterValueByName= (name: string)=> {
//   const results = new RegExp("[?&]" + name + "=([^&#]*)").exec(
//     window.location.href,
//   );
//   if (results == null) {
//     return null;
//   } else if (results[1].toLowerCase() === "nan") return null;
//   else {
//     return decodeURI(results[1]) || 0;
//   }
// },

export const convertToTitleCase = (text: string) => {
  return text
    .toLowerCase()
    .split(" ")
    .map(s => s.charAt(0).toUpperCase() + s.substring(1))
    .join("");
};

export const getApiUrl = () => {
  let uri = $("#hidApiUrl").val() as string;
  if (!uri || uri.length === 0) {
    switch (window.location.hostname.toLowerCase().replace("www.", "")) {
      case "lcl.myteamcare.org":
        uri = "https://api.lclcentralstatesfunds.org";
        break;
      case "dev.myteamcare.org":
        uri = "https://api-dev.centralstatesfunds.org";
        break;
      case "qa.myteamcare.org":
        uri = "https://api-qa.centralstatesfunds.org";
        break;
      case "uat.myteamcare.org":
        uri = "https://api-uat.centralstatesfunds.org";
        break;
      case "crm.myteamcare.org":
        uri = "https://api-crm.centralstatesfunds.org";
        break;
      default:
        uri = "https://api.centralstatesfunds.org";
        break;
    }
  }
  return uri;
};

export const getGqlUrl = () => {
  return `${getApiUrl()}/gql`;
};

export const employerNumberIsValid = (val: string) => {
  val = stripNonDigits(val);
  return /(^\d{11}$)/.test(val);
};

export const getGQLErrorRef = (error: any) => {
  // @ts-ignore fixme
  const { networkError: { result } = {} } = JSON.parse(JSON.stringify(error));
  if (result) {
    return result.ErrorRef;
  }
  return "N/A";
};

export const getGQLErrorMsg = (errorData: any) => {
  let errMsg = "Something unexpected happened, please contact our help desk.";
  // @ts-ignore fixme
  const { networkError: { result: { SystemMessages } = {} } = {} } = JSON.parse(
    JSON.stringify(errorData),
  );

  if (SystemMessages && SystemMessages[0].Message) {
    errMsg = SystemMessages[0].Message;
  }

  return errMsg;
};

export const getGenderLabelFromVal = (genderVal: string) => {
  return genderVal === "F" || genderVal === "Female" ? "Female" : "Male";
};

export const getGenderValFromLabel = (genderLabel: string) => {
  return genderLabel === "Female" || genderLabel === "F" ? "F" : "M";
};

export const getMaritalLabelFromVal = (maritalVal: string) => {
  if (maritalVal) {
    return maritalVal === "M" ? "Married" : "Single";
  }
  return "";
};

export const getMaritalValFromLabel = (maritalLabel: string) => {
  return maritalLabel === "Married" ? "M" : "S";
};

export const getParameterValueByName = (name: string) => {
  const results = new RegExp("[?&]" + name + "=([^&#]*)").exec(
    window.location.href,
  );
  if (results == null) {
    return null;
  } else {
    return decodeURI(results[1]) || null;
  }
};

export const formatCurrency = (
  val: string | number | null,
  addDollarSign?: boolean,
  showDecimals?: boolean,
  showCommas?: boolean,
) => {
  if (typeof showDecimals === "undefined" || showDecimals === null) {
    showDecimals = true;
  }
  if (typeof addDollarSign === "undefined" || addDollarSign === null) {
    addDollarSign = true;
  }
  if (typeof showCommas === "undefined" || showCommas === null) {
    showCommas = true;
  }
  if (typeof val === "string") {
    val = parseFloat(val);
  }
  const decimalPlace = showDecimals ? 2 : 0;
  if (typeof val !== "undefined" && val !== null) {
    return (
      (addDollarSign ? "$" : "") +
      val.toFixed(decimalPlace).replace(/(\d)(?=(\d{3})+\.)/g, "$1,")
    );
  }
  return "";
  //return (addDollarSign ? "$" : "") + val.toFixed(decimalPlace).replace(/(?<=\d)(?=(\d\d\d)+(?!\d))/g, showCommas ? "," : "");
};

export const getRelation = (relation: string) => {
  if (relation === "W" || relation === "H") {
    return "Spouse";
  }
  if (relation === "S") {
    return "Son";
  }
  if (relation === "D") {
    return "Daughter";
  }
  return "";
};

export const getNameString = (user: GenericObject) => {
  if (user) {
    const firstName = user.firstName ?? user.FirstName;
    const middleName = user.middleName ?? user.MiddleName;
    const lastName = user.lastName ?? user.LastName;
    const nameSuffix = user.nameSuffix ?? user.NameSuffix;
    return `${firstName}${
      middleName?.length ? ` ${middleName}.` : " "
    } ${lastName}${nameSuffix?.length ? ` ${nameSuffix}` : ""}`;
  }
  return "";
};

export const isAddtDocsReq = (dependents: EnrollmentDependent[]) => {
  let addtDocsRequired = false;
  if (dependents.length) {
    dependents.forEach(each => {
      if (each.additionalDocumentsRequired) {
        addtDocsRequired = true;
      }
    });
  }

  return addtDocsRequired;
};

export const validateAddress = (
  address: string,
  address2: string,
  city: string,
  state: string,
  zip: string,
): Promise<any> => {
  const a2 =
    address2.length > 0
      ? "&a2=" + encodeURI(address2).replace("#", "%23%20")
      : "";
  const url =
    getApiUrl() +
    "/system/address/verify?a1=" +
    encodeURI(address).replace("#", "%23%20") + // replace - url does not encode hash correctly
    a2 +
    "&city=" +
    encodeURI(city) +
    "&state=" +
    encodeURI(state) +
    "&zip=" +
    encodeURI(zip);
  return new Promise((resolve, reject) => {
    $.ajax({
      type: "GET",
      url: url,
      success: function (data) {
        resolve(data);
      },
      error: function (xhr, ajaxOptions, thrownError) {
        reject(getErrorMessage(xhr, thrownError));
      },
    });
  });
};

export const getEDocumentUrl = (data: { documentTypeId: number }) => {
  return new Promise((resolve, reject) => {
    $.ajax({
      url: `${getApiUrl()}/edocument/url`,
      data: JSON.stringify({ DocumentTypeid: data.documentTypeId }),
      type: "POST",
      dataType: "json",
      contentType: "application/json; charset=utf-8",
      beforeSend: function (xhr) {
        xhr.setRequestHeader("Authorization", "BEARER " + $("#hidToken").val());
      },
      success: function (data) {
        resolve(data);
      },
      error: function (xhr, ajaxOptions, thrownError) {
        reject(getErrorMessage(xhr, thrownError));
      },
    });
  });
};

export const sendActivityTracking = (
  activityId: number,
  activityStatusId: number,
  impersonatorId: number | null,
  userId: string | null,
  summary: string | null,
  umi: string | null,
  input: string | null,
  output: string | null,
  keyValue: string | null,
  userAgent: string | null,
  sourceDescription: string | null,
) => {
  fetch(`${getApiUrl()}/accounts/user-details/activities`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      activityId: activityId,
      activityStatusId: activityStatusId,
      impersonatorId: impersonatorId,
      userId: userId,
      summary: summary,
      umi: umi,
      input: input,
      output: output,
      keyValue: keyValue,
      userAgent: userAgent,
      sourceDescription: sourceDescription,
    }),
  }).catch(() => {});
};

export const getErrorMessage = (
  xhr: JQuery.jqXHR,
  thrownError: string,
  getServerMessage?: boolean,
) => {
  if (typeof xhr !== "undefined" && xhr !== null) {
    if (getServerMessage) {
      return xhr.responseJSON.SystemMessages[0].Message;
    }
    if (xhr.status === 401) {
      return "You are no longer logged in";
    } else if (xhr.status === 404) {
      return "Not found";
    } else if (xhr.status === 403) {
      return "Forbidden";
    } else if (
      typeof xhr.responseJSON !== "undefined" &&
      xhr.responseJSON !== null
    ) {
      if (typeof xhr.responseJSON.ErrorRef !== "undefined") {
        return "Error reference: " + xhr.responseJSON.ErrorRef;
      }
      return xhr.responseJSON.Message;
    } else {
      return xhr.statusText;
    }
  } else {
    return thrownError;
  }
};

export const genderOptions = (gender: string | null) => {
  return [
    (gender === null || (gender && !gender.length)) && {
      label: "Please select your gender",
      value: "",
    },
    {
      value: "Male",
      label: "Male",
    },
    {
      value: "Female",
      label: "Female",
    },
  ].filter(Boolean);
};

export const maritalOptions = () => {
  return [
    {
      value: "Married",
      label: "Married",
    },
    {
      value: "Single",
      label: "Single",
    },
  ];
};

export const getStates = () => {
  const states = [
    { StateCode: "AL", StateName: "Alabama" },
    { StateCode: "AK", StateName: "Alaska" },
    { StateCode: "AZ", StateName: "Arizona" },
    { StateCode: "AR", StateName: "Arkansas" },
    { StateCode: "CA", StateName: "California" },
    { StateCode: "CO", StateName: "Colorado" },
    { StateCode: "CT", StateName: "Connecticut" },
    { StateCode: "DE", StateName: "Delaware" },
    { StateCode: "DC", StateName: "District Of Columbia" },
    { StateCode: "FM", StateName: "Micronesia" },
    { StateCode: "FL", StateName: "Florida" },
    { StateCode: "GU", StateName: "Guam" },
    { StateCode: "GA", StateName: "Georgia" },
    { StateCode: "HI", StateName: "Hawaii" },
    { StateCode: "ID", StateName: "Idaho" },
    { StateCode: "IL", StateName: "Illinois" },
    { StateCode: "IN", StateName: "Indiana" },
    { StateCode: "IA", StateName: "Iowa" },
    { StateCode: "KS", StateName: "Kansas" },
    { StateCode: "KY", StateName: "Kentucky" },
    { StateCode: "LA", StateName: "Louisiana" },
    { StateCode: "ME", StateName: "Maine" },
    { StateCode: "MD", StateName: "Maryland" },
    { StateCode: "MA", StateName: "Massachusetts" },
    { StateCode: "MI", StateName: "Michigan" },
    { StateCode: "MN", StateName: "Minnesota" },
    { StateCode: "MS", StateName: "Mississippi" },
    { StateCode: "MO", StateName: "Missouri" },
    { StateCode: "MT", StateName: "Montana" },
    { StateCode: "NE", StateName: "Nebraska" },
    { StateCode: "NV", StateName: "Nevada" },
    { StateCode: "NH", StateName: "New Hampshire" },
    { StateCode: "NJ", StateName: "New Jersey" },
    { StateCode: "NM", StateName: "New Mexico" },
    { StateCode: "NY", StateName: "New York" },
    { StateCode: "NC", StateName: "North Carolina" },
    { StateCode: "ND", StateName: "North Dakota" },
    { StateCode: "OH", StateName: "Ohio" },
    { StateCode: "OK", StateName: "Oklahoma" },
    { StateCode: "OR", StateName: "Oregon" },
    { StateCode: "PW", StateName: "Palau" },
    { StateCode: "PA", StateName: "Pennsylvania" },
    { StateCode: "RI", StateName: "Rhode Island" },
    { StateCode: "SC", StateName: "South Carolina" },
    { StateCode: "SD", StateName: "South Dakota" },
    { StateCode: "TN", StateName: "Tennessee" },
    { StateCode: "TX", StateName: "Texas" },
    { StateCode: "UT", StateName: "Utah" },
    { StateCode: "VT", StateName: "Vermont" },
    { StateCode: "VA", StateName: "Virginia" },
    { StateCode: "WA", StateName: "Washington" },
    { StateCode: "WV", StateName: "West Virginia" },
    { StateCode: "WI", StateName: "Wisconsin" },
    { StateCode: "WY", StateName: "Wyoming" },
    { StateCode: "AS", StateName: "American Samoa" },
    { StateCode: "MH", StateName: "Marshall Islands" },
    { StateCode: "MP", StateName: "Northern Mariana Islands" },
    { StateCode: "PR", StateName: "Puerto Rico" },
    { StateCode: "VI", StateName: "Virgin Islands" },
  ].sort(function (a, b) {
    const nameA = a.StateName.toUpperCase();
    const nameB = b.StateName.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
  const options = states.map(each => {
    return {
      value: each.StateCode,
      label: each.StateName,
    };
  });
  return options;
};

export const getAddressFromDOM = () => {
  const $address = $(".user_field_address").html() || "";
  const splitAddress = $address.split("<br>");
  if (splitAddress.length < 3) {
    const [address, cityStateZip] = $address.split("<br>");
    const [city, stateZip] = cityStateZip.split(", ");
    const [state, zip] = stateZip.split(" ");
    return [address, "", city, state, zip];
  }
  const [address, address2, cityStateZip] = $address.split("<br>");
  const [city, stateZip] = cityStateZip.split(", ");
  const [state, zip] = stateZip.split(" ");
  return [address, address2, city, state, zip];
};

export const getAddtInsOptions = (content: GenericObject) => {
  return [
    {
      value: "Employer",
      label: "Employer",
    },
    {
      value: content.AdditionalInsuranceTypeOption1,
      label: content.AdditionalInsuranceTypeOption1,
    },
    {
      value: content.AdditionalInsuranceTypeOption2,
      label: content.AdditionalInsuranceTypeOption2,
    },
    {
      value: content.AdditionalInsuranceTypeOption3,
      label: content.AdditionalInsuranceTypeOption3,
    },
  ];
};

export const formatPhone = (phone: string) => {
  let phoneNum = "";
  if (phone) {
    phoneNum = phone.replace(/[^\d]/g, "");
  }
  return phoneNum.replace(/1?(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
};

export const isSpouseAdded = (dependents: EnrollmentDependent[]) => {
  return (
    dependents.filter(
      each =>
        each.relationshipToSubscriber === "W" ||
        each.relationshipToSubscriber === "H",
    ).length > 0
  );
};

export const isValidStrDate = (date: string) => {
  const date_regex =
    /^(?!0+\b)(\d{1,2})[/.-](?!0+\b)(\d{1,2})[/.-](\d{4}|\d{2})$/;
  return date_regex.test(date);
};

export const getLocalDate = (date: Date, dateFormat?: string) => {
  if (!date) {
    return "";
  }
  if (!dateFormat) {
    dateFormat = "MM/dd/yyyy h:mm a";
  }
  return format(subMinutes(date, date.getTimezoneOffset()), dateFormat);
};

//use to format dates in "2022-01-24"
export const formatStrDate = (date: string, dateFormat = "MM/dd/yyyy") => {
  if (!date) {
    return "";
  }
  return format(new Date(date.replace(/-/g, "/")), dateFormat);
};

export const formatISODate = (date: string, dateFormat = "MM/dd/yyyy") => {
  if (!date) {
    return "";
  }
  return format(parseISO(date), dateFormat);
};

export const isEligibleForOpenEnrollment = (
  userContent: GenericObject,
  user: GenericObject,
) => {
  if (
    userContent?.Subscriber?.IsEligibleForOpenEnrollment ||
    user?.Subscriber?.isEligibleForOpenEnrollment
  ) {
    const openEnrollStartDate = format(
      new Date(
        (
          document.getElementById(
            "hidOpenEnrollmentStartDate",
          ) as HTMLInputElement
        ).value,
      ),
      "MM/dd/yyyy",
    );
    const openEnrollEndDate = format(
      new Date(
        (
          document.getElementById(
            "hidOpenEnrollmentEndDate",
          ) as HTMLInputElement
        ).value,
      ),
      "MM/dd/yyyy",
    );
    const currentDate = format(new Date(), "MM/dd/yyyy");

    if (
      isWithinInterval(new Date(currentDate), {
        start: new Date(openEnrollStartDate),
        end: new Date(openEnrollEndDate),
      })
    ) {
      return "eligible";
    } else {
      return "outofrange";
    }
  }
  return "noteligible";
};

export const getPlanOptions = (
  resp: PlanDocumentsResponse[],
  planOptions: EnrollmentSummaryPlanOptions[],
  selectedPlan: string,
) => {
  const [currentPlanDocs] = resp.filter(each => each.Type === "Current");
  const [futurePlanDocs] = resp.filter(each => each.Type === "Future");
  // merge the response with exisiting planOptions info to get
  // group info and documents that to be displayed
  const cPlanDocs = planOptions
    .map(eachPlan => {
      const temp = currentPlanDocs?.PlanContent.filter(
        eachContent => eachContent.PlanCode === eachPlan.planCode,
      )[0];
      return { ...temp, ...eachPlan };
    })
    .map(each => ({
      ...each,
      isCurrentPlan: each.planCode === selectedPlan,
    }));

  const fPlanDocs = futurePlanDocs
    ? planOptions
        .map(eachPlan => {
          const temp = futurePlanDocs.PlanContent.filter(
            eachContent => eachContent.PlanCode === eachPlan.planCode,
          )[0];
          return { ...temp, ...eachPlan };
        })
        .map(each => ({
          ...each,
          isCurrentPlan: each.planCode === selectedPlan,
        }))
    : null;

  const superPlanOptions = futurePlanDocs
    ? cPlanDocs.map((each, key) => {
        return { ...each, futureGroups: fPlanDocs[key].Groups };
      })
    : cPlanDocs;

  return superPlanOptions;
};

export const formatSSN = (ssn: string) => {
  if (ssn && ssn.length === 9) {
    return ssn.slice(0, 3) + "-" + ssn.slice(3, 5) + "-" + ssn.slice(5, 9);
  }
  return ssn;
};

export const getTypesOfCoverages = (
  insurance: EnrollmentSummaryAdditionalInsurance,
  content: GenericObject,
) => {
  return [
    {
      has: insurance.hasMedical,
      content: content.TypesOfCoverageMedical,
    },
    {
      has: insurance.hasDental,
      content: content.TypesOfCoverageDental,
    },
    {
      has: insurance.hasRx,
      content: content.TypesOfCoveragePrescription,
    },
    {
      has: insurance.hasVision,
      content: content.TypesOfCoverageVision,
    },
  ]
    .map(eachContent => {
      if (eachContent.has) {
        return eachContent.content;
      }
    })
    .filter(Boolean);
};

export const anyoneHasHadCoverageSince = (
  member: GenericObject,
  hasHadCoverageSince = `01/01/${new Date().getFullYear() - 2}`,
) => {
  if (member) {
    if (member.MostRecentEligibleDate) {
      if (
        (hasHadCoverageSince &&
          isAfter(
            new Date(member.MostRecentEligibleDate),
            new Date(hasHadCoverageSince),
          )) ||
        isToday(new Date(member.MostRecentEligibleDate))
      ) {
        return true;
      }
    }
    if (
      member.FamilyMembers &&
      member.FamilyMembers.FamilyMember &&
      member.FamilyMembers.FamilyMember.length
    ) {
      let familyMembers: FamilyMember[] = member.FamilyMembers.FamilyMember;
      if (hasHadCoverageSince) {
        familyMembers = familyMembers.filter(
          f =>
            f.MostRecentEligibleDate &&
            isAfter(
              new Date(f.MostRecentEligibleDate),
              new Date(hasHadCoverageSince),
            ),
        );
      } else {
        familyMembers = familyMembers.filter(
          f =>
            f.MostRecentEligibleDate &&
            isToday(new Date(f.MostRecentEligibleDate)),
        );
      }
      return familyMembers.length;
    }
  }
  return false;
};

export const getPlanOptionsTierInfo = (
  enrollmentSummary: EnrollmentSummary,
) => {
  const selectedPlan = enrollmentSummary.summary.selectedPlan;
  const selectedPlanOption =
    enrollmentSummary.planOptions.find(
      each => each.planCode === selectedPlan,
    ) || ({} as EnrollmentSummaryPlanOptions);
  // const selectedPlanTiers = ["Participant and Dependent"];
  const selectedPlanTiers = selectedPlanOption.tiers || [];
  const allowAll =
    selectedPlanTiers.find(each => each === "Family") !== undefined;
  if (allowAll) {
    // [allow all]
    return [true];
  }

  // all deps excluding spouse
  const allowMultipleDeps =
    selectedPlanTiers.find(each => each === "Participant and Dependents") !==
    undefined;

  // excluding spouse
  const allowOneDep =
    selectedPlanTiers.find(each => each === "Participant and Dependent") !==
    undefined;

  const allowSpouseOnly =
    selectedPlanTiers.find(each => each === "Participant and Spouse") !==
    undefined;
  // [allow all, allow multiple dependents,  allow 1 dependent, allow spouse only]
  return [false, allowMultipleDeps, allowOneDep, allowSpouseOnly];
};

export const isParticipantOnlyTier = (enrollmentSummary: EnrollmentSummary) => {
  const selectedPlan = enrollmentSummary.summary.selectedPlan;
  const selectedPlanOption =
    enrollmentSummary.planOptions.find(
      each => each.planCode === selectedPlan,
    ) || ({} as EnrollmentSummaryPlanOptions);
  const selectedPlanTiers = selectedPlanOption.tiers || [];
  return (
    selectedPlanTiers.length == 1 && selectedPlanTiers[0] === "Participant only"
  );
};

export const getFirstSudayOfYear = (year: number) => {
  // @ts-ignore fixme
  const day = getDay(`01/01/${year}`);
  if (day === 0) {
    return `01/01/${year}`;
  } else {
    const date = 8 - day;
    return `01/${date}/${year}`;
  }
};

export const haveNoSpaces = (input: string) => {
  return input.split(" ").length === 1;
};

export const allOnlyNumbers = (input: string) => {
  return !isNaN(+input) && !input.includes(".");
};

export const maskSSN = (ssn: string) => {
  if (!ssn) {
    return "";
  }
  if (ssn.length === 4) {
    return `***-**-${ssn}`;
  }
  return `***-**-${ssn.slice(ssn.length - 4, ssn.length)}`;
};

export const getLabelFromVal = (
  sourceArray: SelectOption[] | SelectOptionNum[],
  value: string | number,
) => {
  let val = "";
  sourceArray.forEach(element => {
    if (element.value === value) val = element.label;
  });

  return val;
};

export const isDisabled = (
  objWithNonEmptyProps: object,
  nonOptinalFieldCount: number,
) => {
  // convert obj to array of property values
  const props = Object.values(objWithNonEmptyProps).filter(Boolean);
  if (props.length !== nonOptinalFieldCount) {
    return true;
  }

  // each property should not be empty
  if (!props.every(each => each || each?.length)) {
    return true;
  }

  return false;
};

export const hasSpecialChars = (str: string) => {
  const asciiCharsPattern = /^[\x20-\x7E]*$/;
  return (
    /[~`!#$%^&*+=\-[\]\\';,.)(/{}|\\":<>_?@]/g.test(str) ||
    !asciiCharsPattern.test(str)
  );
};

export const replaceSpecialChars = (str = "") => {
  return str.replace(/[~`!#$%^&*+=\-[\]\\';,)(/{}|\\":<>_?@]/g, "_");
};

export const getRelationshipToPolicyHolderOptions = (
  content: GenericObject,
) => {
  return [
    {
      value: content.AdditionalInsuranceRelationshipToSubscriberOption1,
      label: content.AdditionalInsuranceRelationshipToSubscriberOption1,
    },
    {
      value: content.AdditionalInsuranceRelationshipToSubscriberOption2,
      label: content.AdditionalInsuranceRelationshipToSubscriberOption2,
    },
    {
      value: content.AdditionalInsuranceRelationshipToSubscriberOption3,
      label: content.AdditionalInsuranceRelationshipToSubscriberOption3,
    },
    {
      value: content.AdditionalInsuranceRelationshipToSubscriberOption4,
      label: content.AdditionalInsuranceRelationshipToSubscriberOption4,
    },
    {
      value: content.AdditionalInsuranceRelationshipToSubscriberOption5,
      label: content.AdditionalInsuranceRelationshipToSubscriberOption5,
    },
    {
      value: content.AdditionalInsuranceRelationshipToSubscriberOption6,
      label: content.AdditionalInsuranceRelationshipToSubscriberOption6,
    },
  ];
};

export const getResidesWithOptions = (content: GenericObject) => {
  return [
    {
      value: content.ChildResidesWithSelection1,
      label: content.ChildResidesWithSelection1,
    },
    {
      value: content.ChildResidesWithSelection2,
      label: content.ChildResidesWithSelection2,
    },
    {
      value: content.ChildResidesWithSelection3,
      label: content.ChildResidesWithSelection3,
    },
    {
      value: content.ChildResidesWithSelection4,
      label: content.ChildResidesWithSelection4,
    },
  ];
};

export const is18YearsOrOlder = (dateOfBirth: string) => {
  if (!dateOfBirth) return false;
  return differenceInYears(new Date(), new Date(dateOfBirth)) >= 18;
};

export const isSonOrDaughter = (relation = "") => {
  return relation === "D" || relation === "S";
};

export const isBirthOfAChildEvent = (specialEnrollmentType = "") => {
  return (
    specialEnrollmentType === "BirthWithinMarriage" ||
    specialEnrollmentType === "BirthOutsideMarriage"
  );
};

export const isDependentRequired = (
  specialEnrollmentType: string,
  lifeEvents: LifeEvent[],
) => {
  if (lifeEvents) {
    return lifeEvents.some(
      event => event.LifeEventDatabaseKey === specialEnrollmentType,
    );
  }
  return false;
};

export const isSuffixValid = (val = "") => {
  return /^[a-zA-Z0-9.]+$/.test(val);
};

export const isValidFileUpload = (files: FileList) => {
  const allowed = new RegExp("(.*?).(jpeg|jpg|pdf|gif|png)$");

  const invalid = Array.from(files).filter(file => {
    return !allowed.test(file.name.trim().toLowerCase());
  });

  return invalid.length === 0;
};

export const isUploadSizeOverLimit = (files: FileList) => {
  let uploadedFilesSize = 0;
  const maxFilesSize = 52428800;

  Array.from(files).forEach(file => {
    uploadedFilesSize += file.size;
  });

  if (uploadedFilesSize > maxFilesSize) {
    return true;
  }

  return false;
};

export const getContinueInterstitialUrl = () => {
  const redirect = getParameterValueByName("returnUrl");
  if (
    typeof redirect !== "undefined" &&
    redirect !== null &&
    redirect.length > 0
  ) {
    return decodeURIComponent(redirect);
  }
  const skipLink = $("a.redirectUrl");
  if (skipLink.length) {
    return ($("a.redirectUrl")[0] as HTMLAnchorElement).href;
  }
  return "/dashboard";
};

export const trackEvent = (name: string, properties = {}) => {
  if (window.heap) {
    window.heap.track(name, properties);
  }
};

export const getDynamicContent = (path: string, className: string) => {
  const url = window.location.origin;
  return new Promise((resolve, reject) => {
    $.ajax({
      type: "POST",
      url: url + "/api/system/GetDynamicContent",
      contentType: "application/json; charset=utf-8",
      dataType: "json",
      data: JSON.stringify({
        path: path,
        className: className,
      }),
      success: function (data) {
        resolve(data);
      },
      error: function (xhr, ajaxOptions, thrownError) {
        reject(getErrorMessage(xhr, thrownError));
      },
    });
  });
};

export const validateFileFormat = (file: File): boolean => {
  const allowed = new RegExp("(.*?).(jpeg|jpg|pdf|gif|png)$");
  return !allowed.test(file.name.trim().toLowerCase());
};

export const validateFileSize = (file: File): boolean => {
  const maxFilesSize = 52428800;
  return file.size > maxFilesSize;
};

export const parseNameValueListToObject = (list: string) => {
  return Object.fromEntries(new URLSearchParams(list));
};

export const getTodayDate = () => {
  return format(new Date(), "MM/dd/yyyy");
};

export const isValidDate = (testDate: string): boolean => {
  const date_regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
  if (!date_regex.test(testDate)) return false;

  const [month, day, year] = testDate.split("/").map(Number);
  const date = new Date(year, month - 1, day);
  return (
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    !isNaN(date.getTime())
  );
};

export const isWithinNextXMonths = (
  dateString: string,
  months = 12,
): boolean => {
  if (!isValidDate(dateString)) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to beginning of the day
  const nextXMonths = addMonths(today, months);
  nextXMonths.setHours(23, 59, 59, 999); // Set to end of the day

  // Parse the date string, assuming it's in MM/DD/YYYY format
  const [month, day, year] = dateString.split("/").map(Number);
  const givenDate = new Date(year, month - 1, day); // month is 0-indexed in JS Date
  givenDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues

  const isOnOrAfterToday = !isBefore(givenDate, today);
  const isOnOrBeforeNextXMonths = !isAfter(givenDate, nextXMonths);

  return isOnOrAfterToday && isOnOrBeforeNextXMonths;
};

export const getRequestOptions = (
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  body?: string,
  accept: "application/json" | "application/xml" = "application/json",
  contentType: "application/json" | "application/xml" = "application/json",
  auth = `Bearer ${$("#hidToken").val()}`,
) => {
  return {
    method,
    headers: {
      Accept: accept,
      ...(body && { "Content-Type": contentType }),
      Authorization: auth,
    },
    ...(body && { body }),
  };
};

export const getFetchUrl = (url: string) => {
  return `${getApiUrl()}${url}`;
};

export const getPayload = <T>(payload: T) => {
  return {
    type: "set-state",
    payload,
  };
};

export const isProduction = () => {
  return window.location.hostname === "myteamcare.org";
};

export const parseObjectToDropdown = (object: {
  [key: string | number]: string | number;
}) => {
  return Object.entries(object).map(([key, value]) => ({
    value: key,
    label: value,
  }));
};
